import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Camera } from "lucide-react";
import { Reveal } from "@/components/ui-custom/Reveal";
import { Eyebrow } from "@/components/ui-custom/Eyebrow";
import { SERVICE_MEDIA } from "@/data/media";

const TOP_BASE = 96;
const GAP = 22;

const StackCard = ({ service, index }) => {
  const ref = useRef(null);
  const media = SERVICE_MEDIA[service.slug];
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.9]);
  const opacity = useTransform(scrollYProgress, [0, 0.85, 1], [1, 1, 0.55]);

  return (
    <div
      ref={ref}
      style={{ position: "sticky", top: `${TOP_BASE + index * GAP}px` }}
      data-testid={`stack-slot-${service.slug}`}
    >
      <motion.div style={{ scale, opacity, transformOrigin: "center top" }}>
        <Link
          to={service.path}
          data-testid={`stack-service-${service.slug}`}
          className="group block relative overflow-hidden"
          style={{
            height: "clamp(400px, 68vh, 560px)",
            borderRadius: "12px",
            border: "1px solid var(--border-dark)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
          }}
        >
          {/* photo background */}
          <div className="absolute inset-0" style={{ background: "var(--charcoal)" }} />
          {media && media.card ? (
            <img
              src={media.card}
              alt={service.cardTitle}
              loading="lazy"
              className="absolute inset-0 group-hover:scale-[1.05]"
              style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.7s ease" }}
            />
          ) : (
            <>
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(80% 70% at 70% 15%, rgba(201,162,75,0.12) 0%, rgba(0,0,0,0) 55%), repeating-linear-gradient(45deg, rgba(255,255,255,0.015) 0px, rgba(255,255,255,0.015) 1px, transparent 1px, transparent 9px)",
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center opacity-[0.08]">
                <Camera size={130} strokeWidth={1} style={{ color: "var(--gold)" }} />
              </div>
            </>
          )}
          {/* legibility overlay */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(13,13,15,0.35) 0%, rgba(13,13,15,0.12) 38%, rgba(13,13,15,0.86) 100%)",
            }}
          />

          {/* content */}
          <div className="relative z-10 h-full flex flex-col justify-between p-8 md:p-11">
            <div className="flex items-start justify-between gap-6">
              <div>
                <span className="gold-numeral block" style={{ fontSize: "clamp(40px, 5vw, 60px)", lineHeight: 1 }}>
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3
                  className="font-head uppercase mt-3"
                  style={{ color: "var(--text-light)", fontSize: "clamp(26px, 3.4vw, 40px)", letterSpacing: "0.015em", lineHeight: 1.05 }}
                >
                  {service.cardTitle}
                </h3>
              </div>
              <span
                className="hidden sm:flex items-center justify-center shrink-0"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 999,
                  background: "var(--gold-gradient)",
                  color: "var(--black)",
                  transition: "transform 0.3s ease",
                }}
              >
                <ArrowUpRight size={26} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </div>

            <div style={{ maxWidth: "44ch" }}>
              <p style={{ color: "rgba(245,242,236,0.9)", fontSize: "clamp(15px, 1.5vw, 18px)", lineHeight: 1.55 }}>
                {service.cardDesc}
              </p>
              <span className="inline-flex items-center gap-2 mt-5 text-gold" style={{ fontWeight: 600, fontSize: "14px", letterSpacing: "0.02em" }}>
                More Details
                <ArrowUpRight size={16} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </div>
          </div>
        </Link>
      </motion.div>
    </div>
  );
};

export const StackingServices = ({
  id,
  eyebrow,
  heading,
  description,
  services,
  dark = false,
  footNote,
  "data-testid": testId,
}) => {
  const sectionClass = dark ? "sec-charcoal section-y" : "sec-offwhite section-y";
  const headingColor = dark ? "var(--text-light)" : "var(--text-dark)";
  const descColor = dark ? "rgba(245,242,236,0.7)" : "var(--text-muted)";

  return (
    <section id={id} className={sectionClass} data-testid={testId}>
      <div className="container-1200">
        <Reveal>
          <div className="max-w-3xl">
            <Eyebrow start>{eyebrow}</Eyebrow>
            <h2 className="h2 mt-5" style={{ color: headingColor }}>{heading}</h2>
            <p className="mt-5" style={{ color: descColor, fontSize: "17px", lineHeight: 1.7, maxWidth: "58ch" }}>
              {description}
            </p>
          </div>
        </Reveal>

        {/* Stacking cards */}
        <div className="mt-12 md:mt-16" style={{ display: "flex", flexDirection: "column", gap: "26px" }}>
          {services.map((s, i) => (
            <StackCard key={s.slug} service={s} index={i} />
          ))}
        </div>

        {footNote ? (
          <p className="mt-10" style={{ color: dark ? "var(--gold)" : "var(--text-muted)", fontSize: "15px" }}>
            {footNote}
          </p>
        ) : null}
      </div>
    </section>
  );
};
